/**
 * Display Text Utility
 * 
 * Provides consistent text formatting and sanitization for displaying
 * user-generated content across the application.
 * 
 * Defense-in-depth: Even though React escapes JSX text and we sanitize
 * on input, this utility ensures consistent display formatting.
 */

/**
 * Sanitize and format text for display
 * - Removes any HTML tags that might have slipped through
 * - Normalizes whitespace
 * - Trims leading/trailing whitespace
 * 
 * @param text - Raw text to display
 * @returns Sanitized text safe for display
 */
export const sanitizeForDisplay = (text: string | null | undefined): string => {
  if (!text) return '';
  
  return text
    // Remove any HTML tags
    .replace(/<[^>]*>/g, '')
    // Normalize whitespace (multiple spaces/newlines to single space)
    .replace(/\s+/g, ' ')
    // Trim
    .trim();
};

/**
 * Format text for display with optional transformations
 * 
 * @param text - Raw text to display
 * @param options - Formatting options
 * @returns Formatted text
 */
export const displayText = (
  text: string | null | undefined,
  options: {
    lowercase?: boolean;
    maxLength?: number;
    ellipsis?: boolean;
  } = {}
): string => {
  const { lowercase = false, maxLength, ellipsis = true } = options;
  
  // Sanitize first
  let result = sanitizeForDisplay(text);
  
  // Apply lowercase if requested
  if (lowercase) {
    result = result.toLowerCase();
  }
  
  // Truncate if maxLength specified
  if (maxLength && result.length > maxLength) {
    result = result.slice(0, maxLength);
    if (ellipsis) {
      result = result.trimEnd() + '…';
    }
  }
  
  return result;
};

/**
 * Format option text for display
 * Max 25 characters for options
 * 
 * @param option - Option label text
 * @returns Formatted option text (max 25 chars)
 */
export const displayOption = (option: string | null | undefined): string => {
  return displayText(option, { maxLength: 25 });
};

/**
 * Format option text as ID (lowercase for matching)
 * Used for option IDs and comparisons
 * 
 * @param option - Option label text
 * @returns Lowercase option ID
 */
export const optionToId = (option: string | null | undefined): string => {
  return displayText(option, { lowercase: true });
};

/**
 * Format question text for display
 * Max 100 characters for questions
 * 
 * @param question - Question text
 * @returns Formatted question text (max 100 chars)
 */
export const displayQuestion = (
  question: string | null | undefined
): string => {
  return displayText(question, { maxLength: 100 });
};

/**
 * Convert text to Title Case (first letter of each word capitalized)
 * Used for displaying options in a consistent format
 * 
 * @param text - Text to convert
 * @param maxLength - Optional max length for truncation
 * @returns Title cased text
 */
export const toTitleCase = (
  text: string | null | undefined,
  maxLength?: number
): string => {
  const sanitized = sanitizeForDisplay(text);
  if (!sanitized) return '';
  
  let result = sanitized
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  
  // Truncate if maxLength specified
  if (maxLength && result.length > maxLength) {
    result = result.slice(0, maxLength).trimEnd() + '…';
  }
  
  return result;
};

/**
 * Format option for display with Title Case
 * Combines sanitization with title case formatting
 * Max 25 characters for options
 * 
 * @param option - Option label text
 * @returns Title-cased option text (max 25 chars)
 */
export const displayOptionTitleCase = (
  option: string | null | undefined
): string => {
  return toTitleCase(option, 25);
};
